// Копирование настройки одного ресторана в другой — чистые правила (19.09).
//
// Заказ пользователя: «скопировал бы роли, доступы, меню из Hide на Патриках в
// Hide на Большой Никитской». Это прямое следствие решения «только логика
// сети»: второй адрес бренда теперь отдельный ресторан, и заполнять его с
// нуля — та же работа второй раз.
//
// Что копирование НЕ решает и обещать не должно: оно закрывает ПЕРВОЕ
// заполнение. Дальше рестораны живут врозь, и правка в одном во второй не
// доедет. Постоянно общее в сети — роли сети (GroupRole, Э5), люди и бот; там,
// где нужна именно связь, а не копия, надо пользоваться ими.

export const CLONE_PARTS = ["access", "menu", "checklists", "venue"] as const;
export type ClonePart = (typeof CLONE_PARTS)[number];

export const CLONE_PART_LABELS: Record<ClonePart, string> = {
  access: "Роли, права и модули",
  menu: "Меню и каталог склада",
  checklists: "Чек-листы",
  venue: "Настройки точки",
};

export const CLONE_PART_HINTS: Record<ClonePart, string> = {
  access: "Роли с их правами и набор включённых модулей. Роли сети не трогаются — они и так общие.",
  menu: "Блюда с ценами и описаниями, позиции склада с категориями. Совпадающие по названию не трогаются.",
  checklists: "Шаблоны чек-листов смены с пунктами, зонами и расписанием.",
  venue: "Ставки смен, окна самобронирования, часовой пояс, шапка бланка. Кроме точки чекина — она своя у каждого адреса.",
};

/** Разобрать список частей из запроса. Пусто или мусор — ошибка у вызывающего. */
export function parseCloneParts(raw: unknown): ClonePart[] {
  if (!Array.isArray(raw)) return [];
  const known = new Set<string>(CLONE_PARTS);
  return [...new Set(raw.filter((v): v is ClonePart => typeof v === "string" && known.has(v)))];
}

/**
 * Порядок применения частей. «Настройки точки» идут ПЕРВЫМИ, потому что
 * заменяют настройки целиком, а «Меню» потом домешивает туда категории склада:
 * в обратном порядке замена стёрла бы только что добавленные категории.
 */
export function orderCloneParts(parts: readonly ClonePart[]): ClonePart[] {
  const order: ClonePart[] = ["venue", "access", "menu", "checklists"];
  return order.filter((p) => parts.includes(p));
}

/** Ключ сравнения «уже есть такое»: регистр и лишние пробелы не в счёт. */
export function nameKey(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Что из названий источника надо завести в цели: те, которых там нет.
 * Совпавшие НЕ переписываются — у цели могла быть своя цена или свой состав,
 * и молча затереть их копированием нельзя.
 */
export function missingNames<T>(source: readonly T[], targetNames: readonly string[], nameOf: (v: T) => string): T[] {
  const have = new Set(targetNames.map(nameKey));
  const seen = new Set<string>();
  const out: T[] = [];
  for (const s of source) {
    const k = nameKey(nameOf(s));
    if (!k || have.has(k) || seen.has(k)) continue;
    seen.add(k);
    out.push(s);
  }
  return out;
}

// ── Настройки точки ──
//
// Настройки точки — это и ставки смен, и окна самобронирования, и шапка
// бланка, но ТАМ ЖЕ лежит `checkin`: координаты двери и QR-токен.
//
// 🔴 Их копировать нельзя, и это не вкусовщина:
//  · координаты — адрес ДРУГОГО ресторана, и геозона чекина встала бы на
//    чужой дом (`src/lib/attendance/geo.ts`);
//  · `qr_token` ищется по всем точкам (`checkin?t=…`), и одинаковый токен у
//    двух ресторанов означает отметку смены не в том ресторане.
//
// Поэтому у цели её собственный `checkin` остаётся нетронутым, а из источника
// он выбрасывается.
export const VENUE_OWN_KEYS = ["checkin"] as const;

export function mergeVenueSettings(source: unknown, target: unknown): Record<string, unknown> {
  const src = isObj(source) ? { ...source } : {};
  const dst = isObj(target) ? { ...target } : {};
  for (const k of VENUE_OWN_KEYS) delete src[k];
  const out: Record<string, unknown> = { ...dst, ...src };
  for (const k of VENUE_OWN_KEYS) {
    if (k in dst) out[k] = dst[k];
    else delete out[k];
  }
  return out;
}

/** Категории склада: добавить недостающие по названию, порядок источника сохранить. */
export function mergeInventoryCategories(source: unknown, target: unknown): { categories: unknown[]; added: number } {
  const src = isObj(source) && Array.isArray(source.inventory_categories) ? source.inventory_categories : [];
  const dst = isObj(target) && Array.isArray(target.inventory_categories) ? target.inventory_categories : [];
  const have = new Set(dst.map((c) => nameKey(categoryName(c))).filter(Boolean));
  const add = src.filter((c) => {
    const k = nameKey(categoryName(c));
    if (!k || have.has(k)) return false;
    have.add(k);
    return true;
  });
  return { categories: [...dst, ...add], added: add.length };
}

function categoryName(c: unknown): string {
  return isObj(c) && typeof c.name === "string" ? c.name : "";
}

function isObj(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}
